import type React from 'react'
import { type FocusEvent, useEffect, useRef, useState } from 'react'

import useDebounce from 'commons/customHooks/useDebounce'
import useOnClickOutside from 'commons/customHooks/useOnClickOutside'
import {
  Button,
  CircularProgress,
  DSButtonType,
  DSInputType,
  Icon,
  Input,
  Typography,
} from 'components'
import { TypesValidations } from 'constants/enums'
import { setFocusMaterialInsertInput } from 'store/StructuralContext/structuralSlice'
import { useDispatch } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import {
  ButtonCampStyled,
  ClientInfo,
  ColumnDivOptionsStyled,
  ContainerLoadingStyled,
  DivRow,
  LabelText,
  OptionsList,
  RowLabels,
  RowOptionStyled,
  SearchContentStyled,
  SubColumnOptionDescription,
  SubColumnOptionStyled,
} from './styles'

interface Props<T> {
  width?: string
  listWidth?: string
  type: 'select' | 'filter'
  placeHolder: string
  minimumInput?: number
  label?: string
  labelAdornment?: React.ReactNode
  labelsConcat?: Array<string>
  valueCase?: 'upper' | 'lower' | 'normal'
  loadOptions?: (inputValue: string, page: string) => Promise<T[]>
  loadOnFocus?: boolean
  optionsLimit?: number
  optionsMaxHeight?: string
  columnsSizeOption?: Array<string>
  keysColumnsOption?: Array<string>
  titlesColumnsOption?: Array<string>
  flexWrapRowOption?: boolean
  flexWrapOptionDescription?: boolean
  clearAfterSelect?: boolean
  page?: number
  setPage?: React.Dispatch<React.SetStateAction<number>>
  setOptionSelected?: (e: T) => void
  buttonSelectOption?: boolean
  isMaterialSearch?: boolean
  isClientSearch?: boolean
  data?: Array<T>
  setDataFiltered?: (data: Array<T>, hasFiltering?: boolean) => void
  disabled?: boolean
  required?: boolean
  onFieldChange?: () => void
  defaultInputValue?: string
  clearInput?: number
  id?: string
  shouldFocusInput?: boolean
  browserAutoComplete?: 'on' | 'off'
  isRightSided?: boolean
  hideIcon?: boolean
}

export const Search = <T extends Record<string, any>>({
  width = '100%',
  listWidth,
  type,
  placeHolder,
  minimumInput = 4,
  label = '',
  labelAdornment,
  labelsConcat = [''],
  valueCase,
  loadOptions,
  loadOnFocus = false,
  optionsLimit = 5,
  optionsMaxHeight = '450px',
  columnsSizeOption = [],
  keysColumnsOption = [],
  titlesColumnsOption = [],
  flexWrapRowOption = false,
  clearAfterSelect = false,
  clearInput = 0,
  page = 0,
  setPage = () => {
    return
  },
  setOptionSelected = () => {
    return
  },
  buttonSelectOption = false,
  isMaterialSearch,
  isClientSearch,
  data = [],
  setDataFiltered = () => {
    return
  },
  disabled = false,
  required = false,
  onFieldChange = () => {
    return
  },
  defaultInputValue = '',
  id,
  shouldFocusInput = false,
  browserAutoComplete = 'off',
  isRightSided = false,
  hideIcon = false,
}: Props<T>) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDebouncing, setIsDebouncing] = useState(false)
  const [dataSelect, setDataSelect] = useState<Array<T>>([])
  const [inputValue, setInputValue] = useState<string>(defaultInputValue)
  const [hasSelection, setHasSelection] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const SearchInputRef = useRef<HTMLInputElement>(null)
  const ColumnDivOptionsRef = useRef<HTMLDivElement>(null)
  const NotFoundDivRef = useRef<HTMLDivElement>(null)
  const SearchContentRef = useRef<HTMLInputElement>(null)
  const OptionListRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(ColumnDivOptionsRef, () => setOpen(false))
  useOnClickOutside(NotFoundDivRef, () => setOpen(false))

  const hasToOpenOptions = () => {
    if (isLoading) {
      setOpen(true)
      return
    }

    if (dataSelect.length > 0) {
      verifySelect(inputValue)
      setOpen(true)
    } else {
      setOpen(false)
    }
  }
  const onFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
    if (loadOnFocus) {
      setOpen(true)
      setIsLoading(true)
      debounceChange(event.target?.value ?? '')
    }
  }

  const verifyInitialStatus = () => {
    setPage(0)
    if (type === 'select') {
      data.length !== 0 ? setDataSelect(data.slice(0, optionsLimit)) : setDataSelect([])
    }
  }

  useEffect(() => {
    verifyInitialStatus()
    if (SearchInputRef.current && shouldFocusInput) {
      SearchInputRef.current.focus()
      SearchInputRef.current.scrollIntoView()
      window.scrollBy(0, -180)
      dispatch(setFocusMaterialInsertInput(false))
    }
  }, [])

  const hasMinimalInput = (input: string) => {
    return input.length >= minimumInput
  }

  const hasValueIncludes = (item: T, value: string) => {
    if (labelsConcat.length > 1) {
      return labelsConcat.find((labelItem) => {
        if (labelItem === 'nr_cnpj_cpf') {
          return String(item[labelItem as keyof T]).includes(
            value.replace(new RegExp('.', 'g'), '').replace(new RegExp('/', 'g'), ''),
          )
        }
        return String(item[labelItem as keyof T])
          .toLowerCase()
          .includes(value.toLowerCase())
      })
    } else {
      return String(item[labelsConcat[0] as keyof T])
        .toLowerCase()
        .includes(value.toLowerCase())
    }
  }

  const getLabelRef = () => {
    return label || labelsConcat.length > 1 ? labelsConcat[0] : ''
  }

  const organizeSelectDataOnScroll = (value: string, dataOptions: Array<T>) => {
    const newData = dataSelect.concat(dataOptions.slice(0, optionsLimit))
    setDataSelect(newData)
    setIsDebouncing(false)
  }

  const organizeSelectData = (value: string, dataOptions: Array<T>) => {
    setDataSelect(dataOptions.slice(0, optionsLimit))
    setIsDebouncing(false)
  }

  const handleIsLoading = (value: string, dataOptions: Promise<T[]>) => {
    setIsLoading(true)

    dataOptions.then((response: Array<T>) => {
      if (Array.isArray(response)) {
        organizeSelectData(value, response)
        setIsLoading(false)
        setIsDebouncing(false)
      } else {
        setDataSelect([])
        setIsLoading(false)
        setIsDebouncing(false)
      }
    })
  }

  const handleIsLoadingOnScroll = (value: string, dataOptions: Promise<T[]>) => {
    dataOptions.then((response: Array<T>) => {
      organizeSelectDataOnScroll(value, response)
    })
  }

  const handleCaseSensitive = (value: string) => {
    let formattedValue = value
    if (valueCase === 'upper') {
      formattedValue = value.toUpperCase()
    } else if (valueCase === 'lower') {
      formattedValue = value.toLowerCase()
    }

    return formattedValue
  }

  const handleLoadOptions = (newValue: string) => {
    const dataOptions = data.length !== 0 ? data : loadOptions && loadOptions(newValue, '0')

    dataOptions instanceof Promise
      ? handleIsLoading(newValue, dataOptions)
      : organizeSelectData(newValue, dataOptions as Array<T>)
  }

  const handleLoadOptionsOnScroll = (newValue: string) => {
    const dataOptions = loadOptions && loadOptions(newValue, (page + 1).toString())

    dataOptions instanceof Promise
      ? handleIsLoadingOnScroll(newValue, dataOptions)
      : organizeSelectDataOnScroll(newValue, dataOptions as unknown as Array<T>)
  }
  const debounceChange = useDebounce(handleLoadOptions, 800)
  const verifySelect = (value: string) => {
    onFieldChange()
    setHasSelection(false)
    const newValue = handleCaseSensitive(value)
    setInputValue(newValue)
    if (hasMinimalInput(newValue)) {
      setOpen(true)
      setIsDebouncing(true)
      debounceChange(newValue)
    } else {
      verifyInitialStatus()
      setIsDebouncing(false)
      setOpen(false)
    }
  }

  const selectOption = (value: T) => {
    verifyInitialStatus()
    setOptionSelected(value)
    if (clearAfterSelect) {
      setInputValue('')
      setHasSelection(false)
    } else {
      if (isClientSearch) {
        setInputValue(labelsConcat[0])
      } else {
        const labelRef = getLabelRef()
        setInputValue(String(value[labelRef as keyof T]))
      }
      setHasSelection(true)
    }
    setOpen(false)
  }

  const verifyFilter = (value: string) => {
    setInputValue(value)
    if (hasMinimalInput(value)) {
      filterData(value)
    } else if (value.length === 0) {
      setDataFiltered([], false)
    }
  }

  const filterData = (value: string) => {
    const newValue = handleCaseSensitive(value)
    const dataFiltered = data.filter((item: T) => hasValueIncludes(item, newValue))
    setDataFiltered(dataFiltered)
  }

  const RenderNoItemFound = () => {
    return (
      <DivRow>
        <Icon color={theme.colors.neutral.high} icon="search-off" size="18" />
        <Typography design="hav-default-03-text">
          Não foi possível encontrar resultados para o termo digitado. Verifique-o e tente
          novamente.
        </Typography>
      </DivRow>
    )
  }

  useEffect(() => {
    const offsetHeight = OptionListRef.current?.offsetHeight || 0
    const scrollHeight = OptionListRef.current?.scrollHeight || 0
    if (offsetHeight < scrollHeight) {
      setHasOverflow(true)
    } else {
      setHasOverflow(false)
    }
  }, [open, isLoading])

  useEffect(() => {
    if (clearInput) setInputValue('')
  }, [clearInput])

  const handleReachBottom = () => {
    handleLoadOptionsOnScroll(inputValue)
    setPage(page + 1)
  }
  const ContainerElement = document.getElementById('myDIV')
  const debounceScroll = useDebounce(handleReachBottom, 500)
  const noMainMaterial =
    /^[\d]{7}$/g.test(inputValue) &&
    dataSelect.length > 0 &&
    dataSelect.every((data) => data?.relacionado === TypesValidations.YES) &&
    !dataSelect.some((d) => d.cd_matr === inputValue)

  return (
    <SearchContentStyled ref={SearchContentRef} width={width}>
      {type === 'select' ? (
        <>
          <Input
            addorment
            browserAutocomplete={browserAutoComplete}
            design={hideIcon ? DSInputType.MASTER_INPUT : DSInputType.SEARCH_INPUT}
            disabled={disabled}
            InputProps={{
              fullWidth: true,
              placeholder: placeHolder,
            }}
            id={id}
            label={label}
            labelAdornment={labelAdornment}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              verifySelect(event.target.value)
            }
            onClick={hasToOpenOptions}
            onFocus={(event) => onFocus(event)}
            ref={SearchInputRef}
            required={required}
            value={inputValue}
          />
          {open && (
            <ColumnDivOptionsStyled
              isRightSided={isRightSided}
              maxwidth={listWidth ? listWidth : `${SearchContentRef.current?.clientWidth}px`}
              optionsmaxheight={optionsMaxHeight}
              ref={ColumnDivOptionsRef}
            >
              {isLoading || isDebouncing ? (
                <ContainerLoadingStyled>
                  <CircularProgress />
                </ContainerLoadingStyled>
              ) : dataSelect.length > 0 ? (
                <>
                  {noMainMaterial && (
                    <DivRow>
                      <Icon color={theme.colors.neutral.high} icon="search-off" size="18" />
                      <Typography design="hav-default-03-text">
                        O código digitado não encontrou materiais disponíveis. Verifique abaixo a
                        lista de sugestões de materiais relacionados.
                      </Typography>
                    </DivRow>
                  )}

                  <RowLabels hasOverflow={hasOverflow}>
                    {titlesColumnsOption.map((option, index) => (
                      <LabelText key={option} width={columnsSizeOption[index]}>
                        {option}
                      </LabelText>
                    ))}
                  </RowLabels>

                  <OptionsList
                    hasOverflow={hasOverflow}
                    id="myDIV"
                    onScroll={() => {
                      if (
                        ContainerElement &&
                        Number(dataSelect[0]['rowscount' as keyof T]) > dataSelect.length &&
                        ContainerElement.scrollTop + 1 >=
                          ContainerElement.scrollHeight - ContainerElement.offsetHeight
                      ) {
                        debounceScroll()
                      }
                    }}
                    ref={OptionListRef}
                  >
                    {dataSelect.map((option, index) => (
                      <RowOptionStyled
                        flexwrap={flexWrapRowOption ? 'wrap' : 'nowrap'}
                        key={`row-option-${index}`}
                        onClick={() => selectOption(option)}
                      >
                        {keysColumnsOption.length > 0 &&
                          keysColumnsOption.map((key, indexColumn) => (
                            <SubColumnOptionStyled
                              key={`sub-column-option-${indexColumn}`}
                              width={columnsSizeOption[indexColumn]}
                            >
                              {isClientSearch ? (
                                <ClientInfo key={`sub-column-span-option-${indexColumn}`}>
                                  <SubColumnOptionDescription>
                                    <strong>Cliente:</strong> {option[key as keyof T]}
                                  </SubColumnOptionDescription>
                                  <SubColumnOptionDescription>
                                    <strong>Código SAP:</strong> {option['cd_clie' as keyof T]}
                                    <span>{'•'}</span>
                                    <strong>CNPJ:</strong> {option['nr_cnpj_cpf' as keyof T]}
                                  </SubColumnOptionDescription>
                                  {option['enderecoFormatado'] && (
                                    <SubColumnOptionDescription>
                                      <strong>Endereço:</strong>{' '}
                                      {option['enderecoFormatado' as keyof T]}
                                    </SubColumnOptionDescription>
                                  )}
                                </ClientInfo>
                              ) : (
                                <SubColumnOptionDescription
                                  key={`sub-column-span-option-${indexColumn}`}
                                >
                                  {isMaterialSearch &&
                                  String(option[key as keyof T]).split(' - ').length > 1
                                    ? String(option[key as keyof T]).split(' - ')[1]
                                    : option[key as keyof T]}
                                </SubColumnOptionDescription>
                              )}
                            </SubColumnOptionStyled>
                          ))}
                        {buttonSelectOption && (
                          <ButtonCampStyled width={columnsSizeOption[3]}>
                            <Button content="Selecionar" design={DSButtonType.SM_PRIMARY_BUTTON} />
                          </ButtonCampStyled>
                        )}
                      </RowOptionStyled>
                    ))}
                    {Number(dataSelect[0]['rowscount' as keyof T]) > dataSelect.length && (
                      <CircularProgress size="20px" />
                    )}
                  </OptionsList>
                </>
              ) : (
                hasMinimalInput(inputValue) && !hasSelection && RenderNoItemFound()
              )}
            </ColumnDivOptionsStyled>
          )}
        </>
      ) : (
        <Input
          addorment
          design={DSInputType.SEARCH_INPUT}
          InputProps={{
            fullWidth: true,
            placeholder: placeHolder,
          }}
          id={id}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            verifyFilter(event.target.value)
          }
          value={inputValue}
        />
      )}
    </SearchContentStyled>
  )
}
